<template>
	<core-layout-content full>
		<core-layout-headline-and-actions>
			<template slot="headline">
				{{ $t("participants") }}
				<core-pagination-info
					:itemsPerPage="itemsPerPage"
					:numberOfItems="listing.numberOfItems"
					:currentPage="page"
				/>
			</template>
			<template slot="actions">
				<j-button v-can="'writeInternals'" :to="{ name: 'InternalsCreate' }">
					<crm-icon name="add" size="small" color="c5" />
					{{ $t("create_participant") }}
				</j-button>
				<j-button
					class="ml2"
					v-can="'writeInternals'"
					:to="{ name: 'InternalsBulkUpload' }"
				>
					<crm-icon name="add" size="small" color="c5" />
					{{ $t("upload_participants") }}
				</j-button>
			</template>
		</core-layout-headline-and-actions>

		<!-- Search -->
		<div class="flex wrap xs-12 xs-left mb2">
			<div class="flex xs-12">
				<j-base-input-search
					:placeholder="$t('search_name_code')"
					:value="searchTerm"
					@input="onSearch"
				/>
				<j-button
					class="ml2"
					type="tertiary"
					v-if="searchTerm"
					@click="cancelSearch"
				>{{ $t("cancel") }}</j-button>
			</div>
		</div>

		<!-- Data Container -->
		<j-card class="bg5">
			<j-data-table
				:header="header"
				:list="computedList"
				:sortable="false"
				:isLoading="isLoading"
				@sort="onSort($event)"
			>
				<template slot="list" slot-scope="{ item }">
					<td v-for="(column, i) in header" :key="i">
						<template>
							<!-- Name -->
							<template v-if="column.field === 'name'">
								<router-link
									:to="{
										name: `InternalsDetail`,
										params: { id: item.id }
									}"
								>{{ item.name }}</router-link>
							</template>

							<!-- Others -->
							<template v-else>{{ item[column.field] }}</template>
						</template>
					</td>
				</template>
				<template slot="empty">{{ $t("errors.no_data") }}</template>
			</j-data-table>
		</j-card>

		<!-- Pagination -->
		<div class="flex xs-12 p4 xs-center mb15">
			<j-pagination
				:amount="listing.numberOfItems"
				:perPage="itemsPerPage"
				:activePage="page"
				@click="onPageChange($event)"
			/>
		</div>
	</core-layout-content>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import JBaseInputSearch from "@/components/JBaseInputSearch/"
import JPagination from "@/components/JPagination/"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import dayjs from "dayjs"
import api from "../api/"
import get from "lodash.get"
import debounce from "lodash.debounce"
import getEmail from "../utils/"

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDataTable,
		JBaseInputSearch,
		JPagination,
		CorePaginationInfo,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions
	},

	data() {
		return {
			isLoading: false,

			// Actual fetched data
			listing: {
				numberOfItems: 0,
				list: []
			},

			// Search term
			searchTerm: null,

			// Pagination
			page: 1,
			itemsPerPage: 30,

			// Header of table
			header: [
				{
					field: "name",
					text: this.$t("name")
				},
				{
					field: "internalCode",
					text: this.$t("code")
				},
				{
					field: "email",
					text: this.$t("email")
				},
				{
					field: "isActive",
					text: this.$t("active_referral")
				},
				{
					text: this.$t("created"),
					field: "createdAt"
				}
			]
		}
	},

	computed: {
		/**
		 * Compute fetch data for data table
		 * This is what we are gonna display
		 */
		computedList() {
			return this.listing.list.map(item => ({
				id: item.id,
				name: item.name,
				email: getEmail(item.contacts).value,
				isActive: item.isActive ? this.$t("yes") : this.$t("no"),
				internalCode: item.internalCode,
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(
							this.$store.state.settings.dateTimeFormat
					  )
					: null
			}))
		}
	},

	methods: {
		onPageChange(pageIndex) {
			this.page = pageIndex
			this.fetchInternals()
			this.scrollToTop()
		},

		scrollToTop() {
			this.$scrollTo("body", 300)
		},

		onSearch: debounce(function(value = null) {
			this.searchTerm = value === null || value === "" ? null : value.trim()
			this.page = 1

			this.fetchInternals()
		}, 200),

		cancelSearch() {
			this.searchTerm = null
			this.page = 1
			this.onSearch()
		},

		async fetchInternals() {
			// Sorting - sort by createdAt
			let sort = { fieldName: "createdAt", type: "desc" }

			// Pagination
			const skip = (this.page - 1) * this.itemsPerPage
			const limit = this.itemsPerPage

			// Search
			let where = null
			if (this.searchTerm)
				where = {
					or: [
						{ name_contains: this.searchTerm },
						{
							internalCode_contains: `/${this.searchTerm}/i`
						}
					]
				}

			this.isLoading = true
			const res = await api.internals({ sort, skip, limit, where })
			this.isLoading = false

			this.listing.list = res.data.internals.list
			this.listing.numberOfItems = res.data.internals.totalCount
		}
	},

	mounted() {
		this.fetchInternals()
	}
}
</script>
